import { Copy } from 'lucide-react';
import {
  PortalDsButton,
  PortalDsField,
  PortalDsInput,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';
import { useCopy } from 'src/hooks/useCopy';
import type { ReportingApiKey, ReportingApiKeyScope } from '../endpoints/reporting-api-keys.endpoints';

export type ApisKeySecretDialogState = {
  title: string;
  description: string;
  keyValue: string;
  clientTitle: string;
  scope?: ReportingApiKeyScope;
};

interface ApisClientKeyDialogsProps {
  clientTitle: string;
  createOpen: boolean;
  createName: string;
  createLoading: boolean;
  secretDialog: ApisKeySecretDialogState | null;
  pendingRevoke: ReportingApiKey | null;
  pendingRotate: ReportingApiKey | null;
  revokeLoading: boolean;
  rotateLoading: boolean;
  onCreateOpenChange: (open: boolean) => void;
  onCreateNameChange: (name: string) => void;
  onCreate: () => void;
  onSecretDialogChange: (state: ApisKeySecretDialogState | null) => void;
  onPendingRevokeChange: (key: ReportingApiKey | null) => void;
  onPendingRotateChange: (key: ReportingApiKey | null) => void;
  onConfirmRevoke: () => void;
  onConfirmRotate: () => void;
}

export function ApisClientKeyDialogs({
  clientTitle,
  createOpen,
  createName,
  createLoading,
  secretDialog,
  pendingRevoke,
  pendingRotate,
  revokeLoading,
  rotateLoading,
  onCreateOpenChange,
  onCreateNameChange,
  onCreate,
  onSecretDialogChange,
  onPendingRevokeChange,
  onPendingRotateChange,
  onConfirmRevoke,
  onConfirmRotate,
}: ApisClientKeyDialogsProps) {
  const { toClipboard } = useCopy();

  return (
    <>
      <Dialog
        open={createOpen}
        onOpenChange={(nextOpen) => {
          onCreateOpenChange(nextOpen);
          if (!nextOpen) {
            onCreateNameChange('');
          }
        }}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Create API key
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              Name the key for
              {' '}
              {clientTitle}
              . The full key is shown once after creation.
            </DialogDescription>
          </DialogHeader>

          <PortalDsField
            label="Name"
            htmlFor="apis-create-key-name"
          >
            <PortalDsInput
              id="apis-create-key-name"
              value={createName}
              placeholder="e.g. Production dashboard"
              required
              onChange={(event) => onCreateNameChange(event.target.value)}
            />
          </PortalDsField>

          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              onClick={() => onCreateOpenChange(false)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="primary"
              size="md"
              disabled={!createName.trim() || createLoading}
              onClick={onCreate}
            >
              {createLoading ? 'Creating…' : 'Create key'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={secretDialog !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            onSecretDialogChange(null);
          }
        }}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              {secretDialog?.title}
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              {secretDialog?.description}
            </DialogDescription>
          </DialogHeader>

          {secretDialog ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#EDEEF2] bg-[#F7F8FA] p-4">
                <p className={cn(portalDs.bodySm, 'mb-2 text-[#5E6278]')}>
                  Client:
                  {' '}
                  {secretDialog.clientTitle}
                </p>
                <div className="flex items-start gap-2">
                  <code className="min-w-0 flex-1 break-all rounded-lg bg-white px-3 py-2 text-[13px] text-[#0D0F1A]">
                    {secretDialog.keyValue}
                  </code>
                  <PortalDsButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="shrink-0"
                    onClick={() => void toClipboard(secretDialog.keyValue, 'API key copied')}
                  >
                    <Copy
                      className="size-3.5"
                      aria-hidden
                    />
                    Copy
                  </PortalDsButton>
                </div>
              </div>

              {secretDialog.scope ? (
                <div className="rounded-xl border border-[#EDEEF2] px-4 py-3">
                  <p className={cn(portalDs.bodySm, 'font-medium text-[#0D0F1A]')}>
                    Resolved scope
                  </p>
                  <p className={cn(portalDs.bodySm, 'mt-1 text-[#5E6278]')}>
                    {secretDialog.scope.sources_count}
                    {' '}
                    sources,
                    {secretDialog.scope.accounts_count}
                    {' '}
                    accounts,
                    {secretDialog.scope.campaigns_count}
                    {' '}
                    campaigns
                  </p>
                </div>
              ) : null}

              <p className={cn(portalDs.bodySm, 'text-[#5E6278]')}>
                Transmit this key securely. Do not share it in email or chat unless your client expects that channel.
              </p>
            </div>
          ) : null}

          <DialogFooter>
            <PortalDsButton
              type="button"
              variant="primary"
              size="md"
              onClick={() => onSecretDialogChange(null)}
            >
              Done
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingRevoke !== null}
        onOpenChange={(nextOpen) => !nextOpen && onPendingRevokeChange(null)}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Disable API key?
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              {pendingRevoke
                ? `Disable "${pendingRevoke.name}" for ${pendingRevoke.client_title}? The key stops working on the next request and remains listed for usage history.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              onClick={() => onPendingRevokeChange(null)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              disabled={revokeLoading}
              className="border-[#FECACA] text-[#DC2626] hover:border-[#FECACA] hover:bg-[#FEF2F2]"
              onClick={onConfirmRevoke}
            >
              {revokeLoading ? 'Disabling…' : 'Disable key'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingRotate !== null}
        onOpenChange={(nextOpen) => !nextOpen && onPendingRotateChange(null)}
      >
        <DialogContent className="rounded-xl border border-[#EDEEF2] bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold tracking-[-0.02em] text-[#0D0F1A]">
              Rotate API key?
            </DialogTitle>
            <DialogDescription className="text-[14px] text-[#5E6278]">
              {pendingRotate
                ? `Issue a replacement for "${pendingRotate.name}" (${pendingRotate.client_title}) and revoke the current key immediately.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="secondary"
              size="md"
              onClick={() => onPendingRotateChange(null)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              type="button"
              variant="primary"
              size="md"
              disabled={rotateLoading}
              onClick={onConfirmRotate}
            >
              {rotateLoading ? 'Rotating…' : 'Rotate key'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
